import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { RQAPICallWithData } from 'store/APIClient';
import qs from 'query-string';
import { Dispatch, useEffect } from 'react';
import { AlertMetric } from 'lib/alert/models';
import { UseQueryParam } from 'react-use/lib/useSearchParam';

/**
 * Get the list of all available metrics for an explore request
 */
export const useGetAvailableMetrics = (
    params: Partial<UseQueryParam> = { initialData: [] },
): UseQueryResult<AvailableMetricT[], Error> =>
    useQuery({
        queryKey: ['get_metrics'],

        queryFn: async () => {
            const response = await RQAPICallWithData<AvailableMetricT[]>({
                endpoint: 'graph/metrics',
                method: 'GET',
            });
            return response.content.data;
        },

        ...params,
    });

const useGetFiltersOrDimensions = <T extends AvailableElementT>(
    endpoint: 'filters' | 'dimensions',
    metrics: (SentenceMetricT | AlertMetric)[],
    enabled: boolean = true,
): UseQueryResult<T[], Error> => {
    const fields = metrics
        .filter((metric) => metric.field)
        .map((metric) => metric.field);
    const queryString = qs.stringify({ fields });
    const uniqueTables = Array.from(new Set(fields)).sort((a, b) =>
        a.localeCompare(b),
    );
    return useQuery({
        queryKey: [endpoint, ...uniqueTables],

        queryFn: async () => {
            const response = await RQAPICallWithData<T[]>({
                endpoint: `graph/${endpoint}?${queryString}`,
                method: 'GET',
            });
            return response.content.data;
        },

        enabled: fields.length > 0 && enabled,
        initialData: [],
    });
};

/**
 * Get the list of all available filters for a given set of metrics.
 */
export const useGetAvailableFilters = (
    metrics: (QueryMetricT | AlertMetric)[],
    enabled: boolean = true,
) => {
    const filtersEndpoint = 'filters';
    return useGetFiltersOrDimensions<AvailableElementT>(
        filtersEndpoint,
        metrics,
        enabled,
    );
};

/**
 * Get the list of all available dimensions for a given set of metrics.
 */
export const useGetAvailableDimensions = (
    metrics: (QueryMetricT | AlertMetric)[],
    enabled: boolean = true,
) => {
    const dimensionsEndpoint = 'dimensions';
    return useGetFiltersOrDimensions<AvailableDimensionT>(
        dimensionsEndpoint,
        metrics,
        enabled,
    );
};

const useSetAvailableElements = (
    elementType: 'metrics' | 'dimensions' | 'filters',
    availableElements: AvailableElementT[],
    setAvailable: Dispatch<React.SetStateAction<AvailableState>>,
) =>
    useEffect(() => {
        setAvailable((available) => ({
            ...available,
            [elementType]: availableElements,
        }));
    }, [availableElements]);

export const useSetAvailableState = (
    availableMetrics: AvailableMetricT[],
    availableDimensions: AvailableDimensionT[],
    availableFilters: AvailableElementT[],
    setAvailable: Dispatch<React.SetStateAction<AvailableState>>,
) => {
    useSetAvailableElements('metrics', availableMetrics, setAvailable);
    useSetAvailableElements('dimensions', availableDimensions, setAvailable);
    useSetAvailableElements('filters', availableFilters, setAvailable);
};
